import type { Money } from '../lib/api.js';
import { formatCompact } from './MoneyText.js';

/**
 * Today's move — the one figure an investor checks before any other.
 *
 * Three rules hold everywhere it appears:
 *
 * 1. **Null is not zero.** A scheme with fewer than two prices on file has an
 *    UNKNOWN move, and rendering that as a confident ₹0 would be a lie the
 *    reader cannot detect. Unknown renders as an em dash.
 * 2. **Never colour alone.** ▲/▼ and the explicit +/− sign carry the direction
 *    for anyone who cannot separate the greens from the reds; the colour is
 *    reinforcement, not the message (dataviz accessibility pass).
 * 3. **One grammar, every surface.** Home's Today card, Wealth's hero, its
 *    section headers and its rows all render through here, so the figure means
 *    the same thing and reads the same way wherever it is met.
 */
export function DayGain({
  money,
  percent = null,
  size = 'inline',
}: {
  money: Money | null;
  percent?: number | null;
  /** `chip` gets the tinted pill; `inline` is bare text for a dense row. */
  size?: 'chip' | 'inline';
}) {
  if (money === null) {
    return (
      <span className="state-muted" title="Needs two prices to know today’s move">
        —
      </span>
    );
  }
  const up = money.amount >= 0;
  const tone = up ? 'delta-pos' : 'delta-neg';
  const body = (
    <>
      {up ? '▲' : '▼'} {up ? '+' : '−'}
      {formatCompact(Math.abs(money.amount))}
      {percent !== null && ` · ${Math.abs(percent).toFixed(2)}%`}
    </>
  );
  if (size === 'chip') {
    return (
      <span className={`badge ${up ? 'badge-good' : 'badge-bad'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {body} today
      </span>
    );
  }
  return (
    <span className={tone} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {body}
    </span>
  );
}
